import React, { useState } from "react";
import { Play } from "lucide-react";

/**
 * Apple-style YouTube embed.
 * Renders a poster thumbnail with a play control; only loads the iframe on click.
 */
export default function VideoEmbed({
  videoId,
  title = "Play video",
  aspect = "aspect-video",
  theme = "dark",
  poster,
  testId = "video-embed",
  className = "",
}) {
  const [playing, setPlaying] = useState(false);
  const thumb =
    poster ||
    `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;

  const isDark = theme === "dark";

  return (
    <div
      data-testid={testId}
      className={`relative ${aspect} w-full overflow-hidden rounded-[28px] md:rounded-[36px] group ${
        isDark
          ? "bg-black border border-white/10 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.7)]"
          : "bg-[#0A0A0A] border border-black/5 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.22)]"
      } ${className}`}
    >
      {playing ? (
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <>
          <img
            src={thumb}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-[1.02] transition-all duration-700"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <button
            onClick={() => setPlaying(true)}
            data-testid={`${testId}-play`}
            aria-label={`Play ${title}`}
            className="absolute inset-0 flex items-center justify-center"
          >
            <span className="relative flex items-center justify-center h-20 w-20 md:h-24 md:w-24 rounded-full bg-white/95 text-black shadow-[0_20px_50px_-10px_rgba(0,0,0,0.5)] transition-transform duration-500 group-hover:scale-110">
              <span className="absolute inset-0 rounded-full bg-white/40 animate-ping opacity-60" />
              <Play size={28} strokeWidth={2} className="relative ml-1" fill="currentColor" />
            </span>
          </button>
          <div className="absolute left-6 bottom-6 md:left-8 md:bottom-8 right-6 md:right-8">
            <div className="text-[10px] uppercase tracking-[0.24em] text-white/70 mb-2">
              Watch
            </div>
            <h3 className="text-white font-display text-xl md:text-2xl lg:text-3xl tracking-tight font-medium max-w-xl leading-tight">
              {title}
            </h3>
          </div>
        </>
      )}
    </div>
  );
}
